import { CheckboxField } from "@aws-amplify/ui-react";
import { Button, FieldActionWrapper, FieldWrapper, Pagination } from "@factorearth/component-library";
import { SortOptions } from "components/FieldHelpers/SortOptions";
import { FieldInstance, FieldTemplate, UserForResolver } from "db/definitions";
import { bulkSaveFieldInstances, createFieldForBatchResolver } from "db/queries/fieldInstanceQueries";
import { Colors } from "@factorearth/component-library/dist/Theme/types";
import useStateRef from "hooks/useStateRef";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FiX, FiList } from "react-icons/fi";
import { UpdateContext } from "utils/UpdateContext";
import batchCreateFieldInstances from "utils/batchCreateFieldInstances";
import batchRetrieveFieldInstances from "utils/batchRetrieveFieldInstances";
import buildManyRows from "utils/buildManyRows";
import deleteRows from "utils/deleteRows";
import getTableFields from "utils/getTableFields";
import { handleBatchCrud } from "utils/handleBatchCrud";
import paginate from "utils/paginate";
import updateTable from "utils/updateTable";
import { v4 as uuid } from "uuid";
import FieldAction from "../../FieldAction/FieldAction";
import TableActionBar from "../TableField/TableActionBar/TableActionBar";
import CardView from "./CardView/CardView";
import styles from "./TableField.module.scss";
import { getFieldsForDuplication } from "./tableFieldModules";
import verifyTableFields from "utils/verifyTableFields";

export interface sortingPriorityObj {
	id: string;
	identifier: string;
	descending: boolean;
	date: boolean;
	createdBy: string;
	rank: number;
}

interface Props {
	color: Colors;
	fieldTemplate: FieldTemplate;
	initialFieldInstance: FieldInstance;
	online: boolean;
	projectId: string;
	setupDrawer: (location: string) => void;
	updateObj: FieldInstance;
	userDoc: UserForResolver;
}

export interface RowHtml {
	key: string;
	row: React.JSX.Element[];
}

function TableField(props: Props) {
	const { color, fieldTemplate, initialFieldInstance, online, projectId, setupDrawer, updateObj, userDoc } = props;
	const { linkedAltIds, linkedData } = useContext(UpdateContext);
	const [tableInstance, setTableInstance] = useStateRef<FieldInstance>(initialFieldInstance);
	const [loaded, setLoaded] = useState(false);
	const [rowsData, setRowsData] = useState<FieldInstance[][]>([]);
	const [rowsHtml, setRowsHtml] = useState<RowHtml[]>([]);

	const headerMap = useMemo(() => {
		const finalMap: Record<string, FieldTemplate> = {};
		if (initialFieldInstance?.fieldTemplate?.tableFields) {
			for (let i = 0; i < initialFieldInstance.fieldTemplate.tableFields.items.length; i++) {
				const tableFieldTemplate = initialFieldInstance.fieldTemplate.tableFields.items[i];
				finalMap[tableFieldTemplate.id] = tableFieldTemplate;
			}
		}
		return finalMap;
	}, [initialFieldInstance.fieldTemplate]);

	const [expanded, setExpanded] = useState(false);
	const [pageIndex, setPageIndex] = useState(1);
	const [checkedRows, setCheckedRows] = useStateRef<string[]>([]);
	const [rowLoading, setRowLoading] = useState(false);
	const [addManyRowsValue, setAddManyRowsValue] = useState<number | undefined>(1);
	const [cardView, setCardView] = useState<string | null>(null);

	const pageSize = 25;
	useEffect(() => {
		async function grabTableFieldsInBatchesAndSetToState() {
			let newTableInstance: FieldInstance = { ...initialFieldInstance };
			if (!initialFieldInstance.id && fieldTemplate.tableFields && fieldTemplate.rowOrder) {
				const newTableFields = [];
				const fieldsOrder = [];
				for (let i = 0; i < fieldTemplate.tableFields.items.length; i++) {
					const tableField = fieldTemplate.tableFields.items[i];
					const fieldIndex = fieldTemplate.rowOrder.indexOf(tableField.id);
					const timestamp = new Date().toISOString();
					const fakeInstance: FieldInstance = {
						value: "",
						fieldTemplate: tableField,
						fieldTemplateId: tableField.id,
						id: i.toString(),
						tableFields: { items: [] },
						formGroup: initialFieldInstance.formGroup,
						fieldsOrder: null,
						notes: "",
						authors: [],
						group: initialFieldInstance.group,
						editing: "",
						createdAt: timestamp,
						updatedAt: timestamp,
					};
					newTableFields.push(fakeInstance);
					fieldsOrder[fieldIndex] = i.toString();
				}
				const finalFieldsOrder = fieldsOrder.join(",");
				newTableInstance.tableFields.items = newTableFields;
				newTableInstance.fieldsOrder = [finalFieldsOrder];
			} else {
				const tableFieldRequestData = await getTableFields(initialFieldInstance.id, online);

				if (tableFieldRequestData) {
					newTableInstance = {
						...initialFieldInstance,
						tableFields: {
							items: tableFieldRequestData,
						},
					};
				}
			}

			// Need to verify that there are no missing rows, in case of new fields in tables
			// and this is not happening during the verification process of the overall form group.
			const verificationResult = await verifyTableFields(fieldTemplate, newTableInstance.tableFields.items, newTableInstance, online, projectId);
			if (verificationResult) {
				newTableInstance = verificationResult;
			}

			setTableInstance(newTableInstance);
			buildRows(newTableInstance, false);
			setLoaded(true);
		}

		grabTableFieldsInBatchesAndSetToState();
	}, [initialFieldInstance]);

	function handleMinWidth(cellTemplate: FieldTemplate) {
		let minWidth = 240;
		if (cellTemplate.action && cellTemplate.action.length > 0) {
			const indexOfAction = cellTemplate.action.indexOf("SET_CELL_WIDTH");
			if (indexOfAction > -1) {
				const actionData = cellTemplate.actionData[indexOfAction];
				minWidth = Number(actionData);
			}
		}

		return minWidth;
	}

	const createCell = useCallback(
		(cell: FieldInstance, cellTemplate: FieldTemplate) => {
			if (!cellTemplate) return null;
			if (cellTemplate.action?.includes("HIDDEN")) return null;
			let minWidth = handleMinWidth(cellTemplate);

			const isLink = !!(cell.altInstanceId && (cellTemplate?.action?.includes("LINK") || cellTemplate?.action?.includes("LINK_TO")));
			const altId = cell.altInstanceId;
			const altFieldInstance: FieldInstance | null = isLink
				? {
						...cell,
						id: altId!,
						value: cell.altInstance?.value ?? cell.value ?? "",
						authors: cell.altInstance?.authors ?? cell.authors ?? [],
						altInstanceId: altId,
					}
				: null;

			return (
				<React.Fragment key={`TableCell-${cell.id}`}>
					<UpdateContext.Consumer>
						{({ updateObj, setupChanged }) => (
							<td
								// className={styles.cell}
								style={{ minWidth: `${minWidth}px` }}>
								<FieldAction
									setupDrawer={setupDrawer}
									setupChanged={setupChanged}
									fromTable={true}
									fieldInstanceId={cell.id}
									updateObj={isLink ? updateObj[altId!] : updateObj[cell.id]}
									fieldTemplate={cellTemplate}
									fieldInstance={isLink ? altFieldInstance : cell}
									online={online}
									userDoc={userDoc}
								/>
							</td>
						)}
					</UpdateContext.Consumer>
				</React.Fragment>
			);
		},
		[online, updateObj, userDoc],
	);

	function buildRows(parentFieldInstance: FieldInstance, toSort: boolean) {
		const final = [];
		const finalRowsData = [];
		const tableMap: Record<string, React.JSX.Element> = {};
		const tableDataMap: Record<string, FieldInstance> = {};
		for (let i = 0; i < parentFieldInstance.tableFields.items.length; i++) {
			const tableField = parentFieldInstance.tableFields.items[i];

			let tableFieldTemplate = tableField.fieldTemplate;
			if (!tableFieldTemplate && tableField.fieldTemplateId) {
				const correctTemplate = headerMap[tableField.fieldTemplateId];
				tableFieldTemplate = correctTemplate;
				tableField.fieldTemplate = correctTemplate;
			}
			if (!tableFieldTemplate) continue;
			if (tableFieldTemplate.action?.includes("HIDDEN")) continue;
			let minWidth = handleMinWidth(tableFieldTemplate);

			const isBuildRowsLink = !!(
				tableField.altInstanceId &&
				(tableFieldTemplate?.action?.includes("LINK") || tableFieldTemplate?.action?.includes("LINK_TO"))
			);
			const buildRowsAltId = tableField.altInstanceId;
			const buildRowsAltFieldInstance: FieldInstance | null = isBuildRowsLink
				? {
						...tableField,
						id: buildRowsAltId!,
						value: tableField.altInstance?.value ?? tableField.value ?? "",
						authors: tableField.altInstance?.authors ?? tableField.authors ?? [],
						altInstanceId: buildRowsAltId,
					}
				: null;

			const html = (
				<React.Fragment key={`TableCell-${tableField.id}`}>
					<UpdateContext.Consumer>
						{({ updateObj, setupChanged }) => (
							<td style={{ minWidth: `${minWidth}px` }}>
								<FieldAction
									setupDrawer={setupDrawer}
									setupChanged={setupChanged}
									fromTable={true}
									fieldInstanceId={tableField.id}
									updateObj={isBuildRowsLink ? updateObj[buildRowsAltId!] : updateObj[tableField.id]}
									fieldInstance={isBuildRowsLink ? buildRowsAltFieldInstance : tableField}
									fieldTemplate={tableFieldTemplate}
									online={online}
									userDoc={userDoc}
								/>
							</td>
						)}
					</UpdateContext.Consumer>
				</React.Fragment>
			);

			tableMap[tableField.id] = html;
			tableDataMap[tableField.id] = tableField;
		}

		if (parentFieldInstance.fieldsOrder) {
			for (let i = 0; i < parentFieldInstance.fieldsOrder.length; i++) {
				const rowOrder = parentFieldInstance.fieldsOrder[i];
				const rowOrderArray = rowOrder.split(",");
				const row = [];
				const theRowsData = [];
				let rowId = rowOrder;
				for (let f = 0; f < rowOrderArray.length; f++) {
					const orderedId = rowOrderArray[f];

					const fieldHtml = tableMap[orderedId];
					const fieldData = tableDataMap[orderedId];

					if (!fieldData) continue; // template was deleted, skip orphaned field
					row.push(fieldHtml);
					theRowsData.push(fieldData);
				}

				const rowHtml = createRow(row, rowId);
				finalRowsData.push(theRowsData);
				final.push(rowHtml);
			}
		}
		if (!toSort) {
			setRowsData(finalRowsData);
			setRowsHtml(final);
		}
		return { rowsArray: finalRowsData, rowsHtml: final };
	}

	useEffect(() => {
		function handleUpdateObj(updateObj: FieldInstance) {
			const tableInstanceCopy = JSON.parse(JSON.stringify(tableInstance));
			if (updateObj && updateObj.fieldsOrder) {
				if (updateObj.fieldsOrder.length > tableInstanceCopy.fieldsOrder.length) {
					const newRows = updateObj.fieldsOrder.slice(tableInstanceCopy.fieldsOrder.length);
					getNewFields(newRows, updateObj.updatedAt);
				} else if (updateObj.fieldsOrder.length < tableInstanceCopy.fieldsOrder.length) {
					handleDeleteEvent(updateObj);
				} else if (updateObj && JSON.stringify(updateObj.fieldsOrder) !== JSON.stringify(tableInstanceCopy.fieldsOrder)) {
					renderRearrange(updateObj);
				}
			}
		}
		if (updateObj && new Date(updateObj.updatedAt) > new Date(tableInstance?.updatedAt) && updateObj.fieldsOrder) {
			handleUpdateObj(updateObj);
		}
	}, [updateObj, tableInstance]);

	function handleDeleteEvent(newFieldInstance: FieldInstance) {
		const filteredHtml = rowsHtml.filter((rowHtml) => newFieldInstance.fieldsOrder!.indexOf(rowHtml.key) > -1);
		const filteredData = rowsData.filter((row) => newFieldInstance.fieldsOrder!.indexOf(row.map((field) => field.id).join(",")) > -1);

		setRowsHtml(filteredHtml);
		setRowsData(filteredData);
		setTableInstance(newFieldInstance);
	}

	async function getNewFields(newRows: string[], updateObjUpdatedAt: string) {
		let newIds: string[] = [];

		for (let i = 0; i < newRows.length; i++) {
			const newRowString = newRows[i];
			if (!newRowString) continue; // skip empty-string entries caused by corrupted fieldsOrder
			const newIdArray = newRowString.split(",").filter((id) => id !== "");
			newIds = newIds.concat(newIdArray);
		}
		if (newIds.length === 0) return;
		const newFields = await handleBatchCrud(newIds, batchRetrieveFieldInstances, 100);

		renderNewRow(newFields, updateObjUpdatedAt, newRows);
	}

	/**
	 * handles a rearrange event from the sub
	 * @param update data from the cloud
	 * @param existing table field data
	 */
	function renderRearrange(update: FieldInstance) {
		const newOrder = update.fieldsOrder;
		if (newOrder) {
			const finalHtml = [];
			const finalData = [];
			const rowMap: Record<string, { row: RowHtml; rowData: FieldInstance[] }> = {};
			const newTableInstance = { ...tableInstance };
			newTableInstance.fieldsOrder = update.fieldsOrder;
			for (let i = 0; i < rowsHtml.length; i++) {
				const row = rowsHtml[i];
				const rowKey = row.key;
				if (rowKey) {
					const rowData = rowsData[i];
					const data = {
						row,
						rowData,
					};
					rowMap[rowKey] = data;
				}
			}
			for (let i = 0; i < newOrder.length; i++) {
				const rowOrder = newOrder[i];
				const rowKey = rowOrder;
				const rowObj = rowMap[rowKey];
				finalHtml.push(rowObj.row);
				finalData.push(rowObj.rowData);
			}

			setRowsHtml(finalHtml);
			setRowsData(finalData);
			setTableInstance(newTableInstance);
		}
	}

	/**
	 * Handles receiving new fields from the cloud and creating a new row in the dom.
	 */
	function renderNewRow(newFieldInstances: FieldInstance[], updatedAt: string, newFieldsOrderRows: string[]) {
		let theTableInstance = { ...tableInstance };
		let final = [...rowsHtml];
		let fieldMap: Record<string, React.JSX.Element> = {};
		let fieldDataMap: Record<string, FieldInstance> = {};

		const newRowData: FieldInstance[][] = [];
		const newTableFields: FieldInstance[] = [];
		if (theTableInstance.fieldsOrder) {
			for (let i = 0; i < newFieldInstances.length; i++) {
				const tableField = newFieldInstances[i];
				let tableFieldTemplate = tableField.fieldTemplate;
				if (!tableFieldTemplate && tableField.fieldTemplateId) {
					tableFieldTemplate = headerMap[tableField.fieldTemplateId];
				}
				if (tableFieldTemplate?.action?.includes("HIDDEN")) continue;

				const html = (
					<UpdateContext.Consumer>
						{({ updateObj, setupChanged }) => (
							<td
								className={styles.cell}
								style={{ borderBottom: `1px solid ${color.border.secondary}`, borderRight: `1px solid ${color.border.secondary}` }}>
								<FieldAction
									setupDrawer={setupDrawer}
									setupChanged={setupChanged}
									fromTable={true}
									fieldInstanceId={tableField.id}
									updateObj={updateObj[tableField.id]}
									fieldInstance={tableField}
									fieldTemplate={tableFieldTemplate}
									online={online}
									userDoc={userDoc}
								/>
							</td>
						)}
					</UpdateContext.Consumer>
				);
				fieldMap[tableField.id] = html;
				fieldDataMap[tableField.id] = tableField;
			}
			let newFieldsOrderItems = [];
			for (let j = 0; j < newFieldsOrderRows.length; j++) {
				const rowString = newFieldsOrderRows[j];
				const rowStringArray = rowString.split(",");
				let orderString = "";
				let row = [];
				let rowData: FieldInstance[] = [];
				for (let i = 0; i < rowStringArray.length; i++) {
					const id = rowStringArray[i];
					const html = fieldMap[id];
					const data = fieldDataMap[id];
					orderString += i === rowStringArray.length - 1 ? id : `${id},`;
					row.push(html);
					rowData.push(data);
					newTableFields.push(data);
				}

				newFieldsOrderItems.push(orderString);
				const rowHtml = createRow(row, orderString);
				final.push(rowHtml);
				newRowData.push(rowData);
				theTableInstance.fieldsOrder.push(orderString);
			}
		}

		theTableInstance.updatedAt = updatedAt;
		for (const cell of newTableFields) {
			theTableInstance.tableFields.items.push(cell);
		}
		const updatedRowsData = rowsData.concat(newRowData);
		setTableInstance(theTableInstance);
		setRowsData(updatedRowsData);
		setRowsHtml(final);
	}

	function handleSelectAll(e: React.ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			const checkedCopy = rowsHtml.map((row) => row.key);
			setCheckedRows(checkedCopy);
		} else {
			setCheckedRows([]);
		}
	}

	/**
	 * Setup the row data for a row.
	 * @param fieldsHtml for the fields in the row.
	 * @param rowKey csv of all the ids of fieldInstances in the row
	 * @returns object with rowkey key, and row containing the html.
	 */
	function createRow(fieldsHtml: React.JSX.Element[], rowKey: string): RowHtml {
		const rowData = {
			key: rowKey,
			row: fieldsHtml,
		};

		return rowData;
	}

	function buildHeaders() {
		const final = [];
		if (fieldTemplate.rowOrder) {
			for (let i = 0; i < fieldTemplate.rowOrder.length; i++) {
				const tableFieldTemplateId = fieldTemplate.rowOrder[i];
				const template = headerMap[tableFieldTemplateId];
				if (template?.action?.includes("HIDDEN")) continue;
				const html = (
					<th key={tableFieldTemplateId} className={styles.header}>
						<div style={{ color: color.text.primary, display: "flex", justifyContent: "space-between" }}>
							<div style={{ margin: "auto 0" }}>{template.header}</div>
							<SortOptions
								columnIndex={i}
								userDoc={userDoc}
								tableInstance={tableInstance}
								setTableInstance={setTableInstance}
								setRowsData={setRowsData}
								setRowsHtml={setRowsHtml}
								online={online}
								tableFieldTemplateId={tableFieldTemplateId}
								buildRows={buildRows}
								setLoaded={setLoaded}
							/>
						</div>
					</th>
				);
				final.push(html);
			}
		}
		return final;
	}

	function handleToggleExpand() {
		setExpanded(!expanded);
		setCardView(null);
	}

	async function handleDeleteSelection() {
		setRowLoading(true);
		const newFieldsOrder = tableInstance.fieldsOrder;
		const newRowsHtml = rowsHtml;
		const newRowsData = [...rowsData];
		const fieldsToDelete = [];
		for (const row of rowsData) {
			let rowString = "";
			for (const cell of row) {
				rowString = rowString ? (rowString += `,${cell.id}`) : cell.id;
			}
			if (checkedRows.indexOf(rowString) !== -1) {
				fieldsToDelete.push(...row);
			}
		}
		if (newFieldsOrder) {
			for (let i = 0; i < checkedRows.length; i++) {
				const rowToDelete = checkedRows[i];
				const fieldsOrderIndex = newFieldsOrder.indexOf(rowToDelete);

				newFieldsOrder.splice(fieldsOrderIndex, 1);
				newRowsHtml.splice(fieldsOrderIndex, 1);
				newRowsData.splice(fieldsOrderIndex, 1);
			}
		}
		if (tableInstance) {
			const newFieldInstance: FieldInstance = {
				...tableInstance,
				fieldsOrder: newFieldsOrder,
			};
			const uniqueFieldsToDelete = [...new Map(fieldsToDelete.map((f) => [f.id, f])).values()];
			await Promise.all([updateTable(newFieldInstance, online), deleteRows(uniqueFieldsToDelete, online)]);
			newFieldInstance.updatedAt = new Date().toISOString();
			setTableInstance(newFieldInstance);
		}
		setRowsHtml(newRowsHtml);
		setRowsData(newRowsData);
		setCheckedRows([]);
		setRowLoading(false);
	}

	/**
	 * Takes the selected rows, duplicating the fields in each row. The duplicated rows appear beneath the selected row they were duplicated from
	 * @author Eric Webb <ewebb@factorearth.com>
	 */
	async function duplicateSelectedRows() {
		setRowLoading(true);
		const tableInstanceCopy = JSON.parse(JSON.stringify(tableInstance));
		const newFieldsOrder = tableInstanceCopy.fieldsOrder;
		const rowsHtmlCopy = [...rowsHtml];
		const rowsDataCopy = [...rowsData];
		const idToInstance = new Map();
		const idToData = new Map();
		const fieldsForBatchResolver = [];
		const fieldsForParentTableInState = [];
		const idsToBatchGet: string[] = checkedRows
			.join()
			.split(",")
			.filter((id) => id !== "");
		const retrievedFieldInstances = online
			? await handleBatchCrud(idsToBatchGet, batchRetrieveFieldInstances, 100)
			: await getFieldsForDuplication(idsToBatchGet);
		for (const field of tableInstance.tableFields.items) {
			idToInstance.set(field.id, field);
		}
		for (let i = 0; i < checkedRows.length; i++) {
			const rowCsv = checkedRows[i];
			const fieldIds = rowCsv.split(",");
			let fieldOrder = "";
			const jsxArray = [];
			const dataRow = [];
			for (let j = 0; j < fieldIds.length; j++) {
				const fieldToCopy = retrievedFieldInstances[i * fieldIds.length + j];
				if (fieldToCopy) {
					const timestamp = new Date().toISOString();
					const id = uuid();
					const fieldCopy: FieldInstance = {
						...fieldToCopy,
						id,
						createdAt: timestamp,
						updatedAt: timestamp,
						tableFields: { items: [] },
					};
					if (fieldCopy.fieldTemplateId) {
						const fieldForBatchResolver = createFieldForBatchResolver(fieldCopy);
						fieldsForBatchResolver.push(fieldForBatchResolver);
						dataRow.push(fieldCopy);
						fieldsForParentTableInState.push(fieldCopy);
						idToData.set(fieldCopy.id, fieldCopy);
						const jsx = createCell(fieldCopy, headerMap[fieldCopy.fieldTemplateId]);
						if (jsx) jsxArray.push(jsx);
						fieldOrder = fieldOrder ? (fieldOrder += `,${id}`) : id;
					}
				} else {
					// What do we do if we can't find the field? Shouldn't ever be an issue though
					console.error("Cannot find field id in array");
				}
			}
			if (!fieldOrder) {
				console.error("duplicateSelectedRows: no valid fields found for row, skipping row insertion");
				continue;
			}
			const indexOfRowCsv = newFieldsOrder.indexOf(rowCsv);
			const rowHtml = createRow(jsxArray, fieldOrder);
			if (indexOfRowCsv !== -1) {
				newFieldsOrder.splice(indexOfRowCsv + 1, 0, fieldOrder);
				rowsHtmlCopy.splice(indexOfRowCsv + 1, 0, rowHtml);
				rowsDataCopy.splice(indexOfRowCsv + 1, 0, dataRow);
			} else {
				// wasn't found, so just add this to the end. Shouldn't ever happen though
				newFieldsOrder.push(fieldOrder);
				rowsHtmlCopy.push(rowHtml);
				rowsDataCopy.push(dataRow);
			}
		}
		const newTable: FieldInstance = {
			...tableInstance,
			fieldsOrder: newFieldsOrder,
			updatedAt: new Date().toISOString(),
			tableFields: {
				items: tableInstance.tableFields.items.concat(fieldsForParentTableInState),
			},
		};
		await Promise.all([
			updateTable(newTable, online),
			online ? handleBatchCrud(fieldsForBatchResolver, batchCreateFieldInstances, 25) : bulkSaveFieldInstances(fieldsForBatchResolver),
		]);
		setRowsHtml(rowsHtmlCopy);
		setRowsData(rowsDataCopy);
		setTableInstance(newTable);
		setCheckedRows([]);
		setRowLoading(false);
	}

	async function handleAddManyRows(
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	): Promise<{ newRowKey: string; newRowData: FieldInstance[] } | undefined> {
		e.preventDefault();
		setRowLoading(true);
		const theTableInstance = JSON.parse(JSON.stringify(tableInstance));
		if (!addManyRowsValue) {
			setRowLoading(false);
			return;
		}
		const newRowsObject = await buildManyRows(addManyRowsValue, theTableInstance, headerMap, online, projectId, linkedAltIds, linkedData);
		const newRows = newRowsObject.fieldsToReturn;
		const fieldsOrder = newRowsObject.parentFieldCopy.fieldsOrder;
		if (fieldsOrder) {
			const finalHtml = [...rowsHtml];
			const finalData = [...rowsData];
			const fieldMap: Record<string, React.JSX.Element> = {};
			const fieldDataMap: Record<string, FieldInstance> = {};

			for (let i = 0; i < newRows.length; i++) {
				const cell = newRows[i];
				let cellTemplate = cell.fieldTemplate;

				if (!cellTemplate && cell.fieldTemplateId) {
					cellTemplate = headerMap[cell.fieldTemplateId];
				}
				if (cellTemplate) {
					const html = createCell(cell, cellTemplate);
					if (html) fieldMap[cell.id] = html;
					fieldDataMap[cell.id] = cell;
				}
			}

			for (let i = theTableInstance.fieldsOrder.length; i < fieldsOrder.length; i++) {
				const rowIds = fieldsOrder[i].split(",");
				const htmlRow = [];
				const dataRow = [];

				for (let j = 0; j < rowIds.length; j++) {
					const id = rowIds[j];
					const html = fieldMap[id];
					const data = fieldDataMap[id];

					htmlRow.push(html);
					dataRow.push(data);
				}

				const rowHtml = createRow(htmlRow, fieldsOrder[i]);
				finalHtml.push(rowHtml);
				finalData.push(dataRow);
			}
			setRowsData(finalData);
			setRowsHtml(finalHtml);
			setTableInstance(newRowsObject.parentFieldCopy);
			setRowLoading(false);
			return {
				newRowKey: fieldsOrder[fieldsOrder.length - 1],
				newRowData: finalData[finalData.length - 1],
			};
		}
		setTableInstance(newRowsObject.parentFieldCopy);
		setRowLoading(false);
	}

	function handleSetCardView() {
		let rowKeyToOpen: string | null = null;
		if (checkedRows.length > 0 && checkedRows.length !== rowsHtml.length) {
			rowKeyToOpen = checkedRows[checkedRows.length - 1];
		} else if (tableInstance?.fieldsOrder && tableInstance.fieldsOrder.length > 0) {
			rowKeyToOpen = tableInstance.fieldsOrder[0];
		} else if (checkedRows.length === rowsHtml.length && checkedRows.length > 0) {
			rowKeyToOpen = checkedRows[0];
		} else {
			// No rows yet — open card view with a sentinel so the user can still add cards
			rowKeyToOpen = "OPEN";
		}

		setCardView(rowKeyToOpen);
	}

	/**
	 * Select a row handler
	 * @param e event data
	 */
	function handleSelectRow(e: React.ChangeEvent<HTMLInputElement>) {
		const rowKey = e.target.value;
		const checkedCopy = [...checkedRows];
		const index = checkedCopy.indexOf(rowKey);
		if (index < 0) {
			checkedCopy.push(rowKey);
		} else {
			checkedCopy.splice(index, 1);
		}
		setCheckedRows(checkedCopy);
	}
	/**
	 * Setup the html to be used in the DOM for a row in a table.
	 * @param fieldsHtml for the fields in the row
	 * @param i index of the row
	 * @returns html
	 */
	function renderRow(fieldsHtml: RowHtml, i: number) {
		const rowKey = fieldsHtml.key;
		const rowChecked = checkedRows.includes(rowKey);
		const rowHtml = (
			<tr
				id={rowKey}
				style={{
					maxHeight: "64px",
					background: i % 2 ? color.background.tableRow : color.background.primary,
					borderBottom: `1px solid ${color.border.primary}`,
				}}
				key={`table-row-${rowKey}`}>
				<td className={styles.options_cell} style={{ paddingLeft: "48px", paddingRight: "32px" }}>
					<div className={styles.options_cell_wrapper}>
						<CheckboxField id={rowKey} name={rowKey} value={rowKey} checked={rowChecked} onChange={handleSelectRow} size="large" label="" />
					</div>
				</td>
				{fieldsHtml.row}
			</tr>
		);
		return rowHtml;
	}

	const paginatedRows = useMemo(() => {
		const rows = paginate({ items: rowsHtml, pageIndex, pageSize }).map((row, index) => renderRow(row, index));
		return rows;
	}, [rowsHtml, pageIndex, pageSize, rowsData, checkedRows, color.theme]);

	return (
		<div className={expanded ? `${styles.wrapper} ${styles.wrapperExpanded}` : styles.wrapper} data-fieldtemplateid={fieldTemplate.id}>
			{expanded && fieldTemplate.rowOrder && tableInstance.fieldsOrder && !cardView ? (
				<>
					<div style={{ backgroundColor: color.background.primary }} className={`${styles.wrapperExpanded}`}>
						<div className={styles.header_and_actionBar}>
							<div style={{ color: color.text.primary }} className={styles.table_headerExpand}>
								<div className={styles.table_headerExpand_top}>
									<div id="table-header" style={{ color: color.text.primary, fontWeight: "bold" }}>
										Table - {fieldTemplate.header}
									</div>
									<div data-test={"open-table-" + fieldTemplate.id} className={styles.close_btn} onClick={handleToggleExpand}>
										<FiX />
									</div>
								</div>
								<div className={styles.table_headerExpand_bottom} style={{ color: color.text.primary }}>
									<div className={styles.headerText}>{!cardView && `${fieldTemplate.rowOrder.length} Column(s)`}</div>
									<div className={styles.headerText} data-test={`table-entry-count-${fieldTemplate.id}`}>
										{tableInstance.fieldsOrder.length === 1 ? "1 Entry" : `${tableInstance.fieldsOrder.length} Entries`}
									</div>
								</div>
							</div>
							<>
								<TableActionBar
									addManyRowsValue={addManyRowsValue}
									checkedRows={checkedRows}
									color={color}
									duplicateSelectedRows={duplicateSelectedRows}
									handleAddManyRows={handleAddManyRows}
									handleDeleteSelection={handleDeleteSelection}
									handleSetCardView={handleSetCardView}
									loaded={loaded}
									rowLoading={rowLoading}
									setAddManyRowsValue={setAddManyRowsValue}
									setupDrawer={setupDrawer}
								/>
								<Pagination
									currentPage={pageIndex}
									totalPages={Math.ceil(rowsHtml.length / pageSize)}
									setCurrentPage={setPageIndex}
									colorPalette={color}
								/>
							</>
						</div>
						{loaded && !cardView ? (
							<>
								<div
									className={styles.tableWrapper}
									style={{
										scrollbarWidth: "auto",
										scrollbarColor: `${color.border.primary} transparent`,
									}}>
									<table
										style={{
											display: "table",
											height: "fit-content", // Fails on scss
											minWidth: "100%",
										}}
										className={`${styles.table} ${styles.tableExpanded}`}>
										<thead
											style={{
												position: "sticky",
												top: "0",
												backgroundColor: color.background.primary,
												zIndex: 1000,
												borderBottom: `1px solid ${color.border.primary}`,
											}}>
											<tr
												style={{
													color: color.text.primary,
												}}
												className={styles.rowHeader}
												key={"headers"}>
												<th key={"buffer-header"} className={styles.buffer} style={{ paddingLeft: "48px", paddingRight: "32px" }}>
													<CheckboxField
														name="select-all-checkbox"
														id="select-all-checkbox"
														size="large"
														onChange={handleSelectAll}
														checked={checkedRows.length === rowsHtml.length}
														label=""
													/>
												</th>
												{buildHeaders()}
											</tr>
										</thead>
										<tbody>{paginatedRows}</tbody>
									</table>
								</div>
							</>
						) : (
							<div style={{ color: color.text.primary, textAlign: "center" }}>Loading {tableInstance.fieldsOrder?.length} rows...</div>
						)}
					</div>
				</>
			) : (
				!expanded && (
					<FieldWrapper colorPalette={color} label={`Table - ${fieldTemplate.header}`}>
						<FieldActionWrapper>
							<div className={styles.headerInfo}>
								<div style={{ color: color.text.primary }} className={styles.rowCount}>
									{loaded ? `Row Count: ${tableInstance.fieldsOrder?.length}` : `Loading ${tableInstance.fieldsOrder?.length} Rows...`}
								</div>
								<Button
									colorPalette={color}
									variant={"outlined"}
									onClick={handleToggleExpand}
									data-test={"open-table-" + fieldTemplate.id}
									icon={<FiList />}>
									{expanded ? "X" : "Open Table"}
								</Button>
							</div>
						</FieldActionWrapper>
					</FieldWrapper>
				)
			)}
			{/* BEGIN CARD VIEW */}
			{cardView && tableInstance?.fieldsOrder ? (
				<div style={{ backgroundColor: color.background.primary, flexGrow: 1, overflow: "auto" }}>
					<div style={{ color: color.text.primary }} className={styles.table_headerExpand}>
						<div className={styles.table_headerExpand_top}>
							<div id="table-header" style={{ color: color.text.primary, fontWeight: "bold" }}>
								Table - {fieldTemplate.header}
							</div>
							<div data-test={"open-table-" + fieldTemplate.id} className={styles.close_btn} onClick={handleToggleExpand}>
								<FiX />
							</div>
						</div>
						<div className={styles.table_headerExpand_bottom} style={{ color: color.text.primary }}>
							<div className={styles.headerText}>{!cardView && `${fieldTemplate?.rowOrder?.length} Column(s)`}</div>
							<div className={styles.headerText} data-test={`table-entry-count-${fieldTemplate.id}`}>
								{tableInstance.fieldsOrder.length === 1 ? "1 Entry" : `${tableInstance.fieldsOrder.length} Entries`}
							</div>
						</div>
					</div>
					<CardView
						color={color}
						fieldsOrder={tableInstance.fieldsOrder}
						initial={cardView}
						online={online}
						rowsData={rowsData}
						setCardView={setCardView}
						setupDrawer={setupDrawer}
						userDoc={userDoc}
						handleAddManyRows={handleAddManyRows}
						rowLoading={rowLoading}
					/>
				</div>
			) : null}
		</div>
	);
}

export default TableField;
